<template>
  <FullScreenPopup
    ref="popup"
    :title="getPopupModeResources.title"
    :lock-scroll="true"
    @visibility-change="handleVisibilityChange"
    @default-selected="handleSubmit"
  >
    <div class="task-popup">
      <PopupSubtitle>{{ getPopupModeResources.name.subtitle }}</PopupSubtitle>
      <TextInput
        class="task-name"
        :placeholder="getPopupModeResources.name.placeholder"
        :value="task.name"
        v-model="task.name"
      />

      <PopupSubtitle>{{ getPopupModeResources.description.subtitle }}</PopupSubtitle>
      <TextAriaInput
        class="task-description"
        :placeholder="getPopupModeResources.description.placeholder"
        :value="task.description"
        v-model="task.description"
      />

      <div class="task-date flex align-center justify-between">
        <PopupSubtitle class="task-date__subtitle">{{ getPopupModeResources.date.subtitle }}</PopupSubtitle>
        <PopupBox id="popup-create-task-calendar" placement="top-end" @visibility-change="handleFinishDateCalendarVisibilityChange">
          <template v-slot:trigger>
            <div class="task-date__date flex align-center">
              <span class="task-date__text mr-3">{{ formatDateTime(task.finishDate, 'DD.MM.YYYY') }}</span>
              <CalendarSmallIcon />
              <PopupToggler :is-open="finishDateCalendarVisible" color="#FB923C" class="ml-2" />
            </div>
          </template>
          <template>
            <PopupCalendar
              popup-id="popup-create-task-calendar"
              :disabled-past-dates="true"
              :options="{ eventMap: taskPopupPeriodCalendarEventMap }"
            />
          </template>
        </PopupBox>
      </div>

      <PopupSubtitle>{{ getPopupModeResources.file.subtitle }}</PopupSubtitle>
      <FilesList
        ref="filesList"
        v-if="isTaskHasAttachment"
        class="mb-3"
        :files="task.attachments"
        :show-delete-file="dialogMode === 'edit'"
        :download-link-generator="generateDownloadLink"
      />
      <Dropzone
        ref="dropzone"
        class="task-file"
        upload-url="/tasks/{itemId}/attachments"
        accepted-file-types="image/*,application/pdf"
        :max-files="100"
        :auto-process-queue="false"
        @file-rejected="$emit('file-rejected', $event)"
        @upload-error="$emit('upload-error', $event)"
      />

      <AccentButton
        class="task-popup__button flex align-center justify-center"
        :is-loading="controls.processing"
        :disabled="!isTaskValid"
        @click="handleSubmit"
      >
        <PlusIcon color="#FFFFFF" class="mr-2" />{{ getPopupModeResources.submit }}
      </AccentButton>
    </div>
  </FullScreenPopup>
</template>

<script>
import { taskPopupModes, taskPopupPeriodCalendarEventMap, TASKS_POPUP_PERIOD_EVENTS } from '@/features/tasks';
import { taskAttachmentDownloadLinkGenerator } from '@/features/tasks/lib';
import { dateTimeFormatter } from '@/shared/lib/date-time';
import { Dropzone, FilesList } from '@/shared/ui/file-upload';
import { AccentButton, TextAriaInput, TextInput } from '@/shared/ui/form';
import { PopupSubtitle } from '@/shared/ui/full-screen-popup';
import { CalendarSmallIcon, PlusIcon } from '@/shared/ui/icons';
import { FullScreenPopup, PopupBox, PopupCalendar, PopupToggler } from '@/shared/ui/popup';

export default {
  data() {
    return {
      dialogMode: 'create',
      task: {
        id: '',
        name: '',
        description: '',
        finishDate: null,
        attachments: [],
      },
      controls: {
        processing: false,
      },
      finishDateCalendarVisible: false,
      taskPopupPeriodCalendarEventMap,
    };
  },
  created() {
    this.$eventBus.$on(TASKS_POPUP_PERIOD_EVENTS.TASK_FINISH_DATE, this.handleTaskFinishDateChange);
  },
  beforeDestroy() {
    this.$eventBus.$off(TASKS_POPUP_PERIOD_EVENTS.TASK_FINISH_DATE, this.handleTaskFinishDateChange);
  },
  mixins: [ dateTimeFormatter, taskAttachmentDownloadLinkGenerator ],
  components: { FullScreenPopup, PopupSubtitle, TextInput, TextAriaInput, CalendarSmallIcon, AccentButton, PlusIcon,
    PopupBox, PopupCalendar, PopupToggler, Dropzone, FilesList },
  computed: {
    getPopupModeResources() { return taskPopupModes[this.dialogMode]; },
    getPopupMode() { return this.dialogMode; },
    isProcessing() { return this.controls.processing; },
    isTaskValid() { return !!(this.task.name && this.task.description && this.task.finishDate); },
    isTaskHasAttachment() { return this.task?.attachments?.length; },
  },
  methods: {
    open(mode = 'create', task = { id: '', name: '', description: '', finishDate: '', attachments: [] }) {
      this.dialogMode = mode;
      this.task = task;
      this.controls.processing = false;
      this.$refs?.filesList?.clearMarkedDeletedFiles();

      this.$refs.popup.open();
    },
    togglePopup() {
      this.$refs.popup.togglePopup();
    },
    close() {
      this.$refs.popup.close();
    },
    getMarkedForDeleteFiles() {
      return this.$refs?.filesList?.getMarkedForDeleteFiles || [];
    },
    setProcessing(processing = true) {
      this.controls.processing = processing;
    },
    async uploadFilesToServer(taskId) {
      await this.$refs.dropzone.processQueue(taskId);
    },
    handleVisibilityChange({ id, isVisible }) {
      if (id === this.$refs.popup.getPopupId) {
        this.$emit('visibility-change', { id, isVisible });
      }
    },
    handleTaskFinishDateChange(date) {
      this.task.finishDate = date;
    },
    handleFinishDateCalendarVisibilityChange({ isVisible }) {
      this.finishDateCalendarVisible = isVisible;
    },
    handleSubmit() {
      this.dialogMode === 'create'
        ? this.$emit('task-create-new', this.task)
        : this.$emit('task-update', this.task);
    },
  },
};
</script>

<style lang="scss" scoped>
.task-popup {
  min-width: 500px;

  @media (max-width: 550px) {
    min-width: auto;
  }

  &__button {
    width: 100%;
    margin-top: 16px;
  }
}

.task {
  &-description {
    min-height: 150px;
  }

  &-date {
    margin-bottom: 16px;

    &__subtitle {
      margin-bottom: 0;
    }

    &__text {
      font-size: 20px;
      font-weight: 600;
      color: #FB923C;
    }
  }
}
</style>
